import "pages/StandardTreatment/shared/masterData.scss";
import "pages/MasterPengobatanStandar/index.scss";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { confirmAlert } from "react-confirm-alert";
import { toast } from "react-toastify";
import { FiPlus } from "react-icons/fi";
import Button from "components/atoms/Button";
import ActionButton from "components/atoms/ActionButton";
import InputField from "components/atoms/InputField";
import InputSelect from "components/atoms/InputSelect";
import TableDataComponent from "components/organisms/TableData";
import standardTreatmentService, { errorMessages } from "services/StandardTreatment";
import useServiceData from "../shared/useServiceData";
import useBootstrapModal from "../shared/useBootstrapModal";
import RuleFormModal, { RULE_MODAL_ID } from "../shared/RuleFormModal";
import { ACTIVE_FILTERS, ErrorState, ReadOnlyBanner, VersionStatusPill, formatDateTime, useDebounced } from "../shared/ui";

const LIMIT = 10;
export const ruleDetailUrl = (id) => `/standard-treatment/rules/${id}`;

// Rules selalu dalam konteks satu Versi: GET /standard-treatment-rules?version_id=…
const Rules = () => {
  const history = useHistory();
  const versionId = new URLSearchParams(useLocation().search).get("version_id");
  const [code, setCode] = useState("");
  const [isActive, setIsActive] = useState("");
  const [page, setPage] = useState(1);
  const sourceRuleCode = useDebounced(code.trim().toUpperCase());
  const ruleModal = useBootstrapModal(RULE_MODAL_ID);

  useEffect(() => {
    document.title = "SISMAL | Daftar Rules";
  }, []);

  const versions = useServiceData(() => standardTreatmentService.listVersions({ page: 1, limit: 100 }), []);
  const active = useServiceData(() => standardTreatmentService.getActiveRelease(), []);
  const versionList = versions.data?.version_rules || [];
  const version = versionList.find((v) => v.id === versionId);
  const isDraft = version?.status === "DRAFT";

  // Default: Versi aktif, atau versi terbaru bila belum ada rilis.
  useEffect(() => {
    if (versionId || versions.loading || active.loading) return;
    const fallback = active.data?.version_id || versionList[0]?.id;
    if (fallback) history.replace(`/standard-treatment/rules?version_id=${fallback}`);
  }, [versionId, versions.loading, active.loading, active.data, versionList, history]);

  useEffect(() => setPage(1), [versionId, sourceRuleCode, isActive]);

  const rules = useServiceData(
    () =>
      versionId
        ? standardTreatmentService.listRules({
            version_id: versionId,
            page,
            limit: LIMIT,
            source_rule_code: sourceRuleCode || undefined,
            is_active: isActive || undefined,
          })
        : Promise.resolve(null),
    [versionId, page, sourceRuleCode, isActive]
  );
  const summary = useServiceData(
    () => (versionId ? standardTreatmentService.getRulesSummary(versionId) : Promise.resolve(null)),
    [versionId, rules.data]
  );

  const versionOptions = versionList.map((v) => ({
    label: `Versi ${v.version} · ${v.status}${v.id === active.data?.version_id ? " · aktif" : ""}`,
    value: v.id,
  }));

  const deleteRule = (rule) =>
    confirmAlert({
      title: `Hapus Rule ${rule.source_rule_code} #${rule.source_rule_seq}?`,
      message: "Rule beserta Kondisi dan Terapinya dihapus dari Draft ini.",
      buttons: [
        {
          label: "Ya, hapus",
          onClick: () =>
            standardTreatmentService
              .deleteRule(rule.id)
              .then(() => {
                toast.success("Rule dihapus");
                rules.reload();
              })
              .catch((err) => toast.error(errorMessages(err).join(" · "))),
        },
        { label: "Batal" },
      ],
    });

  const columns = useMemo(
    () => [
      { header: "Kode", accessorKey: "source_rule_code", size: 80, cell: ({ getValue }) => <strong>{getValue()}</strong> },
      { header: "Seq", accessorKey: "source_rule_seq", size: 60 },
      {
        header: "Nama Rule",
        accessorKey: "name",
        size: 320,
        cell: ({ row }) => (
          <Link className="table-link" to={ruleDetailUrl(row.original.id)}>
            {row.original.name}
          </Link>
        ),
      },
      { header: "Prioritas", accessorKey: "priority", size: 80 },
      {
        header: "Status",
        accessorKey: "is_active",
        size: 90,
        cell: ({ getValue }) => (
          <span className={`mps-pill ${getValue() ? "is-success" : "is-neutral"}`}>{getValue() ? "Aktif" : "Nonaktif"}</span>
        ),
      },
      { header: "Diperbarui", accessorKey: "updatedAt", size: 150, cell: ({ getValue }) => formatDateTime(getValue()) },
      {
        header: "Aksi",
        size: 170,
        cell: ({ row }) => (
          <div className="row-actions">
            <ActionButton btnType="detail" onClick={() => history.push(ruleDetailUrl(row.original.id))} />
            {isDraft && <ActionButton btnType="delete" onClick={() => deleteRule(row.original)} />}
          </div>
        ),
      },
    ],
    [isDraft, rules.reload]
  );
  const tableInstance = useReactTable({
    data: rules.data?.standard_treatment_rules || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const createRule = (body) =>
    standardTreatmentService.createRule({ version_id: versionId, ...body }).then((rule) => {
      ruleModal.close();
      toast.success(`Rule ${rule.source_rule_code} #${rule.source_rule_seq} dibuat`);
      history.push(ruleDetailUrl(rule.id));
    });

  return (
    <div className="master-data-page">
      <h1 className="page-title">Daftar Rules</h1>
      <p className="page-subtitle">
        Rule selalu milik satu Versi. Pilih Versi, lalu buka Rule untuk melihat Kondisi dan Terapinya.
      </p>

      {versions.error ? (
        <ErrorState messages={versions.error} onRetry={versions.reload} />
      ) : (
        <>
          <div className="action-wrapper">
            <div className="action-filters">
              <div className="action-search">
                <InputSelect
                  label="Versi"
                  isClearable={false}
                  placeholder={versions.loading ? "Memuat versi..." : "Pilih versi"}
                  data={versionOptions}
                  value={versionOptions.find((o) => o.value === versionId) || null}
                  onChange={(sel) => sel?.value && history.push(`/standard-treatment/rules?version_id=${sel.value}`)}
                />
              </div>
              <div className="action-status">
                <InputField
                  label="Kode rule (tepat)"
                  placeholder="Contoh: A18"
                  maxLengthVal={20}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
              </div>
              <div className="action-status">
                <InputSelect
                  label="Status rule"
                  isClearable={false}
                  data={ACTIVE_FILTERS}
                  value={ACTIVE_FILTERS.find((o) => o.value === isActive)}
                  onChange={(sel) => setIsActive(sel?.value ?? "")}
                />
              </div>
            </div>
            {isDraft && (
              <div className="align-self-end">
                <Button variant="primary" size="normal" onClick={() => ruleModal.open("add")}>
                  <FiPlus size={16} className="mr-2" />
                  Tambah Rule
                </Button>
              </div>
            )}
          </div>

          {version && (
            <div className="d-flex flex-wrap align-items-center mt-3" style={{ gap: 8 }}>
              <VersionStatusPill status={version.status} />
              {summary.data && (
                <span className="mps-muted">
                  {summary.data.rule_count} rule · {summary.data.condition_count} kondisi · {summary.data.therapy_count} terapi
                </span>
              )}
              <Link className="table-link ml-auto" to={`/master-pengobatan-standar?version_id=${version.id}`}>
                Buka konfigurasi versi →
              </Link>
            </div>
          )}
          {version && !isDraft && (
            <div className="mt-3">
              <ReadOnlyBanner version={version} />
            </div>
          )}

          {rules.error ? (
            <div className="mt-3">
              <ErrorState messages={rules.error} onRetry={rules.reload} />
            </div>
          ) : (
            <TableDataComponent
              columnLength={columns.length}
              tableInstance={tableInstance}
              onPageChange={(next) => next && setPage(next)}
              loading={rules.loading || versions.loading}
              currentPage={rules.data?.currentPage ?? page}
              limit={LIMIT}
              nextPage={rules.data?.nextPage}
              previousPage={rules.data?.previousPage}
              total={rules.data?.total ?? 0}
            />
          )}
        </>
      )}

      <RuleFormModal key={ruleModal.modal.openCount} mode="add" onSubmit={createRule} />
    </div>
  );
};

export default Rules;
